/**
 * Page size for paginated reads. Kept at/below Supabase's default `max_rows`
 * cap (1000) so a single page is never silently truncated by PostgREST.
 */
export const DEFAULT_PAGE_SIZE = 500;

export interface PageResponse<T> {
  data: T[] | null;
  error: { message: string } | null;
}

/**
 * Fetches every row of a Supabase query, working around the PostgREST
 * `max_rows` cap that silently truncates large unbounded `select`s.
 *
 * A PostgREST query builder can only be awaited once, so the caller supplies a
 * factory that applies `.range(from, to)` to a *fresh* query on each call. The
 * query MUST carry a stable `.order(...)` on a unique column (e.g. the primary
 * key); without one, rows can repeat or be skipped across page boundaries.
 *
 * Pages are fetched sequentially until a short page signals the end.
 */
export async function fetchAllPages<T>(
  buildPageQuery: (from: number, to: number) => PromiseLike<PageResponse<T>>,
  pageSize: number = DEFAULT_PAGE_SIZE
): Promise<T[]> {
  const rows: T[] = [];

  for (let from = 0; ; from += pageSize) {
    const { data, error } = await buildPageQuery(from, from + pageSize - 1);
    if (error) throw error;
    if (!data || data.length === 0) break;

    rows.push(...data);
    if (data.length < pageSize) break;
  }

  return rows;
}
